import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import ID from '../../types/id';

/** The minimum shape a typeahead option needs. */
export type TypeaheadOption = { id: ID; name?: string | null };

/**
 * Generic search-as-you-type autocomplete.
 *
 * The control holds a string while the user is typing and the selected option
 * object once they pick one, so consumers that filter by ID only need to look
 * for an object value. Typing over a selection discards it (`cleared` fires).
 *
 * <typeahead
 *   label="Auditor"
 *   [control]="form.controls.auditor"
 *   [search]="searchAuditors"
 *   (cleared)="apply()"
 * ></typeahead>
 */
@Component({
  selector: 'typeahead',
  templateUrl: './typeahead.component.html',
  styleUrls: ['./typeahead.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TypeaheadComponent<T extends TypeaheadOption = TypeaheadOption>
  implements OnInit, OnDestroy
{
  @Input() label = '';
  @Input() placeholder = '';
  @Input() control: FormControl<T | string | null> = new FormControl<
    T | string | null
  >(null);
  /** Returns the options matching the typed term. Keep it stable (bind a property, not a new function per render). */
  @Input({ required: true }) search!: (term: string) => Observable<T[]>;
  @Input() displayWith: (option: T | null) => string = (option) =>
    option?.name ?? '';
  @Input() debounce = 300;
  @Input() minChars = 1;

  /** Emits when the user picks an option. */
  @Output() selected = new EventEmitter<T>();
  /** Emits when the selection is removed, via the clear button or by typing over it. */
  @Output() cleared = new EventEmitter<void>();

  private _options = new BehaviorSubject<T[]>([]);
  public options$ = this._options.asObservable();
  private _loading = new BehaviorSubject<boolean>(false);
  public loading$ = this._loading.asObservable();

  private hasSelection = false;
  private sub: Subscription;

  // arrow function so it can be handed straight to [displayWith] on mat-autocomplete
  public display = (option: T | null) => this.displayWith(option);

  ngOnInit() {
    this.hasSelection = this.isOption(this.control.value);
    this.sub = this.control.valueChanges
      .pipe(
        tap((value) => this.handleValue(value)),
        filter((value): value is string => typeof value === 'string'),
        debounceTime(this.debounce),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < this.minChars) {
            this._loading.next(false);
            return of([] as T[]);
          }
          this._loading.next(true);
          return this.search(term.trim()).pipe(
            catchError(() => of([] as T[])),
          );
        })
      )
      .subscribe((options) => {
        this._loading.next(false);
        this._options.next(options);
      });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  public clear(event?: Event) {
    // the field is inside a mat-form-field; don't let the click focus/open it
    event?.stopPropagation();
    this._options.next([]);
    this.control.setValue(null);
  }

  public showClear(loading: boolean | null): boolean {
    const value = this.control.value;
    return (
      !this.control.disabled &&
      !loading &&
      value !== null &&
      value !== undefined &&
      value !== ''
    );
  }

  private handleValue(value: T | string | null) {
    if (this.isOption(value)) {
      this.hasSelection = true;
      this.selected.emit(value);
    } else if (this.hasSelection) {
      this.hasSelection = false;
      this.cleared.emit();
    }
  }

  private isOption(value: T | string | null | undefined): value is T {
    return value !== null && value !== undefined && typeof value !== 'string';
  }
}
